/**
 * „Caută / adaugă partener" — un singur câmp, comun tuturor ecranelor în care se alege un client
 * (spec parteneri comuni, §5). Rezultatele vin din trei surse, în ordinea asta:
 *
 *  1. CRM — clienții pe care îi vezi (agentul: doar ai lui; serverul impune asta);
 *  2. ExpertAccounts — partenerii care nu sunt încă în CRM: „Folosește" îi aduce, legați de EA,
 *     fără să trimită nimic. EA are o limită de cereri (una la câteva secunde), deci se întreabă
 *     doar la cerere sau când CRM-ul n-a găsit nimic;
 *  3. ANAF — pentru un cod fiscal care nu e nicăieri: precompletează fereastra de adăugare.
 *
 * Ultima linie deschide fereastra comună „Adaugă client nou". După salvare, partenerul e ales.
 *
 * Lista se randează în `document.body`: câmpul stă des într-o fereastră cu `overflow-y-auto`, care
 * ar tăia-o după primul rând.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { AddClientModal, type PrecompletarePartener } from '../orders/AddClientModal'

export interface PunctPartener {
  id: number
  nume: string
  adresa: string | null
  localitate: string | null
  judet: string | null
  codPostal: string | null
  telefon: string | null
  persoana: string | null
}

export interface Partener {
  id: number
  nume: string
  cif: string | null
  adresa: string | null
  localitate: string | null
  judet: string | null
  codPostal: string | null
  telefon: string | null
  persoanaContact: string | null
  puncte: PunctPartener[]
}

interface PropunereAnaf {
  cui: string
  denumire: string | null
  nrRegCom: string | null
  adresa: string | null
  localitate: string | null
  judet: string | null
  codPostal: string | null
  tva: boolean | null
}

interface RezultatEa {
  denumire: string
  cif: string
  grup: string
  agent: string | null
  localitate: string | null
  judet: string | null
  stare: 'crm' | 'altAgent' | 'disponibil'
  clientId: number | null
}

interface Pozitie {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

const MIN_SPATIU = 220
const MAX_INALTIME = 380

const errMsg = (e: unknown, implicit: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? implicit

export function CautaAdaugaPartener({
  baza,
  value,
  onChange,
  disabled,
  grupuri,
  grupImplicit,
  toti,
  placeholder = 'denumirea sau codul fiscal…',
  rolNou = 'client',
}: {
  /** Routerul ecranului: `/orders` (Comenzi) sau `/marketing/necesar` (Necesar). */
  baza: string
  /** Partenerul ales. Doar `id` și `nume` sunt garantate. */
  value: { id: number; nume: string } | null
  onChange: (p: Partener | null) => void
  disabled?: boolean
  /** Grupurile din fereastra de adăugare. */
  grupuri?: { value: string; label: string }[]
  grupImplicit?: string
  /**
   * Lista deja încărcată a ecranului (ex. clienții agentului la Comenzi): se arată la deschidere,
   * fără să tastezi, și se filtrează pe loc. Căutarea pe server rămâne pentru cod fiscal și EA.
   */
  toti?: { id: number; nume: string }[]
  placeholder?: string
  /**
   * Ce se creează din „Adaugă partener nou": un client (implicit) sau un furnizor — la PR &
   * Evenimente, organizatorul pe care îl plătim. Ruta e aceeași, `${baza}/parteneri/nou`.
   */
  rolNou?: 'client' | 'furnizor'
}) {
  const [text, setText] = useState('')
  const [crm, setCrm] = useState<Partener[]>([])
  const [anaf, setAnaf] = useState<PropunereAnaf | null>(null)
  const [cauta, setCauta] = useState(false)
  const [ea, setEa] = useState<RezultatEa[] | null>(null)
  const [eaCauta, setEaCauta] = useState(false)
  const [eaIndisponibil, setEaIndisponibil] = useState(false)
  const [eaNeconfigurat, setEaNeconfigurat] = useState(false)
  const [lucreaza, setLucreaza] = useState(false)
  const [deschis, setDeschis] = useState(false)
  const [fereastra, setFereastra] = useState<PrecompletarePartener | null>(null)
  const [poz, setPoz] = useState<Pozitie | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listaRef = useRef<HTMLDivElement>(null)
  const eaPentru = useRef('')

  const q = text.trim()

  // 1. CRM (+ propunerea ANAF), la fiecare text.
  useEffect(() => {
    setEa(null)
    setEaIndisponibil(false)
    if (q.length < 2) {
      setCrm([])
      setAnaf(null)
      setCauta(false)
      return
    }
    let anulat = false
    setCauta(true)
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ crm: Partener[]; anaf: PropunereAnaf | null }>(
          `${baza}/parteneri/cauta`,
          { params: { q } },
        )
        if (!anulat) {
          setCrm(r.data.crm)
          setAnaf(r.data.anaf)
        }
      } catch {
        if (!anulat) {
          setCrm([])
          setAnaf(null)
        }
      } finally {
        if (!anulat) setCauta(false)
      }
    }, 300)
    return () => {
      anulat = true
      clearTimeout(t)
    }
  }, [q, baza])

  async function cautaInEa(termen: string) {
    if (termen.length < 3) return
    eaPentru.current = termen
    setEaCauta(true)
    try {
      const r = await api.get<{ ea: RezultatEa[]; indisponibil: boolean; neconfigurat?: boolean }>(
        `${baza}/parteneri/ea`,
        {
          params: { q: termen },
        },
      )
      if (eaPentru.current !== termen) return
      setEa(r.data.ea)
      setEaIndisponibil(r.data.indisponibil)
      setEaNeconfigurat(!!r.data.neconfigurat)
    } catch {
      if (eaPentru.current === termen) setEaIndisponibil(true)
    } finally {
      if (eaPentru.current === termen) setEaCauta(false)
    }
  }

  // 2. EA, singur, doar când CRM-ul n-a găsit nimic (și omul s-a oprit din scris).
  useEffect(() => {
    if (cauta || q.length < 3 || crm.length > 0 || anaf) return
    const t = setTimeout(() => void cautaInEa(q), 900)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cauta, q, crm.length, anaf])

  useEffect(() => {
    function click(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || listaRef.current?.contains(t)) return
      setDeschis(false)
    }
    document.addEventListener('mousedown', click)
    return () => document.removeEventListener('mousedown', click)
  }, [])

  useLayoutEffect(() => {
    if (!deschis) {
      setPoz(null)
      return
    }
    const aseaza = () => {
      const r = wrapRef.current?.getBoundingClientRect()
      if (!r) return
      const subCamp = window.innerHeight - r.bottom - 8
      const pesteCamp = r.top - 8
      const inJos = subCamp >= MIN_SPATIU || subCamp >= pesteCamp
      setPoz({
        left: r.left,
        width: Math.max(r.width, 360),
        ...(inJos ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }),
        maxHeight: Math.min(MAX_INALTIME, Math.max(inJos ? subCamp : pesteCamp, 160)),
      })
    }
    aseaza()
    window.addEventListener('scroll', aseaza, true)
    window.addEventListener('resize', aseaza)
    return () => {
      window.removeEventListener('scroll', aseaza, true)
      window.removeEventListener('resize', aseaza)
    }
  }, [deschis])

  function alege(p: Partener) {
    onChange(p)
    setDeschis(false)
    setText('')
  }

  async function alegeDupaId(id: number) {
    setLucreaza(true)
    try {
      const r = await api.get<{ partener: Partener }>(`${baza}/parteneri/${id}`)
      alege(r.data.partener)
    } catch (e) {
      toast.error(errMsg(e, 'Partenerul nu a putut fi încărcat.'))
    } finally {
      setLucreaza(false)
    }
  }

  async function foloseste(r: RezultatEa) {
    setLucreaza(true)
    try {
      const res = await api.post<{ partener: Partener }>(`${baza}/parteneri/din-ea`, {
        denumire: r.denumire,
      })
      toast.success(`„${res.data.partener.nume}" a fost adus din ExpertAccounts.`)
      alege(res.data.partener)
    } catch (e) {
      toast.error(errMsg(e, 'Partenerul nu a putut fi adus din ExpertAccounts.'))
    } finally {
      setLucreaza(false)
    }
  }

  function deschideFereastra() {
    const cod = /^\s*(ro)?\s*\d{2,10}\s*$/i.test(q)
    setFereastra(
      anaf
        ? {
            cif: anaf.cui,
            name: anaf.denumire ?? '',
            regCom: anaf.nrRegCom ?? '',
            address: anaf.adresa ?? '',
            city: anaf.localitate ?? '',
            county: anaf.judet ?? '',
            postcode: anaf.codPostal ?? '',
            vatPayer: anaf.tva,
            // Din ANAF: fara numar la ONRC, fereastra pune singura bifa „Nu e la Registrul Comertului".
            sursa: 'anaf',
          }
        : cod
          ? { cif: q }
          : { name: q },
    )
    setDeschis(false)
  }

  // In `document.body`: campul sta des intr-un <label> sau intr-o alta fereastra.
  const fereastraJsx = createPortal(
    <AddClientModal
      open={fereastra !== null}
      onClose={() => setFereastra(null)}
      endpoint={`${baza}/parteneri/nou`}
      rol={rolNou}
      precompletare={fereastra}
      grupuri={grupuri}
      grupImplicit={grupImplicit}
      onCreated={(id) => {
        setFereastra(null)
        void alegeDupaId(id)
      }}
    />,
    document.body,
  )

  if (value) {
    return (
      <>
        <div className="flex w-full items-center gap-1 rounded border border-b4l-border bg-b4l-bg-soft px-2 py-1.5">
          <span className="flex-1 truncate text-sm" title={value.nume}>
            {value.nume}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs text-b4l-text-muted hover:text-b4l-red"
              title="Alege alt partener"
            >
              ✕
            </button>
          )}
        </div>
        {fereastraJsx}
      </>
    )
  }

  const locale =
    toti && q.length < 2
      ? toti.slice(0, 50)
      : toti
        ? toti.filter((c) => c.nume.toLowerCase().includes(q.toLowerCase())).slice(0, 50)
        : []
  const idsCrm = new Set(crm.map((c) => c.id))
  const doarLocale = locale.filter((c) => !idsCrm.has(c.id))
  const eaDeArata = (ea ?? []).filter((r) => !(r.clientId && idsCrm.has(r.clientId)))

  const sectiune = (titlu: string) => (
    <div className="sticky top-0 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-b4l-text-muted">
      {titlu}
    </div>
  )

  const lista = poz && (
    <div
      ref={listaRef}
      style={{
        position: 'fixed',
        left: poz.left,
        width: poz.width,
        top: poz.top,
        bottom: poz.bottom,
        maxHeight: poz.maxHeight,
      }}
      className="z-[70] overflow-auto rounded border border-gray-200 bg-white shadow-lg"
    >
      {q.length < 2 && !toti && (
        <div className="px-3 py-2 text-xs text-gray-400">
          Scrie denumirea partenerului. Merge și codul fiscal, cu sau fără RO.
        </div>
      )}

      {/* 1. CRM */}
      {(crm.length > 0 || doarLocale.length > 0 || cauta) && sectiune('În CRM')}
      {cauta && crm.length === 0 && doarLocale.length === 0 && (
        <div className="px-3 py-2 text-sm text-gray-400">Se caută…</div>
      )}
      {crm.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => alege(r)}
          className="block w-full border-b border-gray-100 px-3 py-1.5 text-left hover:bg-gray-50"
        >
          <div className="truncate text-sm">{r.nume}</div>
          <div className="text-[11px] text-b4l-text-muted">
            {r.cif ?? 'fără cod fiscal'}
            {r.localitate ? ` · ${r.localitate}` : ''}
            {r.puncte.length > 0 &&
              ` · ${r.puncte.length} ${r.puncte.length === 1 ? 'punct de lucru' : 'puncte de lucru'}`}
          </div>
        </button>
      ))}
      {doarLocale.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => void alegeDupaId(c.id)}
          className="block w-full border-b border-gray-100 px-3 py-1.5 text-left text-sm hover:bg-gray-50"
        >
          {c.nume}
        </button>
      ))}

      {/* 3. ANAF */}
      {anaf && (
        <>
          {sectiune('Din ANAF — nu e în CRM')}
          <button
            type="button"
            onClick={deschideFereastra}
            className="block w-full border-b border-gray-100 px-3 py-1.5 text-left hover:bg-gray-50"
          >
            <div className="truncate text-sm">{anaf.denumire}</div>
            <div className="text-[11px] text-b4l-text-muted">
              {anaf.cui}
              {anaf.localitate ? ` · ${anaf.localitate}` : ''} · se deschide fereastra de adăugare,
              completată
            </div>
          </button>
        </>
      )}

      {/* 2. ExpertAccounts */}
      {q.length >= 3 && (
        <>
          {sectiune('În ExpertAccounts')}
          {eaCauta && <div className="px-3 py-2 text-sm text-gray-400">Se caută în EA…</div>}
          {!eaCauta && ea === null && !eaIndisponibil && (
            <button
              type="button"
              onClick={() => void cautaInEa(q)}
              className="block w-full px-3 py-1.5 text-left text-sm text-b4l-blue hover:bg-gray-50"
            >
              Caută „{q}” și în ExpertAccounts
            </button>
          )}
          {!eaCauta && eaIndisponibil && (
            <div className="px-3 py-2 text-xs text-b4l-text-muted">
              {eaNeconfigurat
                ? 'Citirea din ExpertAccounts nu e configurată (Setări → Integrări → ExpertAccounts).'
                : 'ExpertAccounts nu răspunde acum (are o limită de cereri). Încearcă din nou peste câteva secunde.'}{' '}
              <button
                type="button"
                className="text-b4l-blue hover:underline"
                onClick={() => void cautaInEa(q)}
              >
                Reîncearcă
              </button>
            </div>
          )}
          {!eaCauta && ea !== null && eaDeArata.length === 0 && !eaIndisponibil && (
            <div className="px-3 py-2 text-xs text-gray-400">
              Niciun partener în ExpertAccounts.
            </div>
          )}
          {!eaCauta &&
            eaDeArata.map((r) => (
              <div
                key={r.denumire}
                className="flex items-center gap-2 border-b border-gray-100 px-3 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{r.denumire}</div>
                  <div className="text-[11px] text-b4l-text-muted">
                    {r.cif || 'fără cod fiscal'}
                    {r.localitate ? ` · ${r.localitate}` : ''}
                    {r.grup ? ` · ${r.grup}` : ''}
                  </div>
                </div>
                {r.stare === 'crm' && r.clientId ? (
                  <button
                    type="button"
                    disabled={lucreaza}
                    onClick={() => void alegeDupaId(r.clientId!)}
                    className="shrink-0 rounded border border-b4l-border px-2 py-0.5 text-xs hover:bg-gray-50"
                  >
                    Alege
                  </button>
                ) : r.stare === 'disponibil' ? (
                  <button
                    type="button"
                    disabled={lucreaza}
                    onClick={() => void foloseste(r)}
                    className="shrink-0 rounded bg-b4l-blue px-2 py-0.5 text-xs text-white disabled:opacity-50"
                    title="Îl aduce în CRM, legat de fișa din ExpertAccounts. Nu trimite nimic."
                  >
                    Folosește
                  </button>
                ) : (
                  <span className="shrink-0 text-[11px] text-b4l-amber-dark">
                    E clientul lui {r.agent ?? 'alt agent'}
                  </span>
                )}
              </div>
            ))}
        </>
      )}

      <button
        type="button"
        onClick={deschideFereastra}
        className="block w-full border-t border-gray-200 bg-b4l-bg-soft px-3 py-2 text-left text-sm hover:bg-gray-100"
      >
        <span className="font-medium">
          {rolNou === 'furnizor' ? '+ Adaugă furnizor nou' : '+ Adaugă partener nou'}
        </span>
        <span className="block text-[11px] text-b4l-text-muted">
          {rolNou === 'furnizor'
            ? 'Intră la furnizori și se trimite imediat în ExpertAccounts.'
            : 'Se trimite imediat în ExpertAccounts, cu punctele de lucru.'}
        </span>
      </button>
    </div>
  )

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setDeschis(true)
        }}
        onFocus={() => setDeschis(true)}
        disabled={disabled || lucreaza}
        placeholder={lucreaza ? 'Se încarcă…' : placeholder}
        className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm disabled:bg-gray-50"
      />
      {deschis && !disabled && lista && createPortal(lista, document.body)}
      {fereastraJsx}
    </div>
  )
}
